import { Head, useForm, Link, router } from "@inertiajs/react"
import { ArrowLeft, Camera, X, Sparkles } from "lucide-react"
import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { samplesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Combobox } from "@/components/ui/combobox"

const processOptions = [
  { value: "natural", label: "Natural" },
  { value: "washed", label: "Lavado" },
  { value: "honey", label: "Honey" },
]

const varietalOptions = [
  { value: "typica", label: "Typica" },
  { value: "caturra", label: "Caturra" },
  { value: "catuai", label: "Catuaí" },
  { value: "gesha", label: "Gesha" },
  { value: "bourbon_amarillo", label: "Bourbon Amarillo" },
  { value: "bourbon_rosado", label: "Bourbon Rosado" },
  { value: "solok", label: "Solok" },
]

interface Cupping {
  id: number
  title: string
}

interface Props {
  cuppings: Cupping[]
}

export default function NewSample({ cuppings }: Props) {
  const { t } = useTranslation()
  const [picturePreview, setPicturePreview] = useState<string | null>(null)
  const [newPicture, setNewPicture] = useState<File | null>(null)
  const [analyzingImage, setAnalyzingImage] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("samples.breadcrumb"),
      href: samplesPath(),
    },
    {
      title: t("samples.new.page_title"),
      href: "/samples/new",
    },
  ]

  const { data, setData, processing, errors } = useForm({
    name: "",
    description: "",
    origin: "",
    land: "",
    producer: "",
    process: "",
    variety: "",
    cupping_id: "",
  })

  const handlePictureChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (file) {
      setNewPicture(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPicturePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const analyzeImage = async () => {
    if (!picturePreview) return

    setAnalyzingImage(true)
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content")
      const response = await fetch("/api/sample_analysis", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          image: picturePreview,
          language: localStorage.getItem("i18nextLng") || "es",
        }),
      })

      const result = await response.json()

      if (result.error) {
        console.error("Analysis error:", result.error)
        return
      }

      // Prefill form fields with AI analysis results
      if (result.name) setData("name", result.name)
      if (result.origin) setData("origin", result.origin)
      if (result.land) setData("land", result.land)
      if (result.producer) setData("producer", result.producer)
      if (result.process) setData("process", result.process)
      if (result.variety) setData("variety", result.variety)
      if (result.description) setData("description", result.description)
    } catch (error) {
      console.error("Failed to analyze image:", error)
    } finally {
      setAnalyzingImage(false)
    }
  }

  const removePicture = () => {
    setNewPicture(null)
    setPicturePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData()
    formData.append("sample[name]", data.name)
    formData.append("sample[description]", data.description)
    formData.append("sample[origin]", data.origin)
    formData.append("sample[land]", data.land)
    formData.append("sample[producer]", data.producer)
    formData.append("sample[process]", data.process)
    formData.append("sample[variety]", data.variety)
    if (data.cupping_id) {
      formData.append("sample[cupping_id]", data.cupping_id)
    }
    if (newPicture) {
      formData.append("sample[picture]", newPicture)
    }
    router.post(samplesPath(), formData as any)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("samples.new.page_title")} />

      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Link href={samplesPath()}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <CardTitle>{t("samples.new.title")}</CardTitle>
                <CardDescription>
                  {t("samples.new.description")}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    {t("samples.new.fields.name_required")}
                  </Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={(e) => setData("name", e.target.value)}
                    placeholder={t("samples.new.fields.name_placeholder")}
                    required
                  />
                  {errors.name && (
                    <p className="text-destructive text-sm">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cupping_id">
                    {t("samples.new.fields.cupping")}
                  </Label>
                  <Select
                    value={data.cupping_id}
                    onValueChange={(value) => setData("cupping_id", value)}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "samples.new.fields.cupping_placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {cuppings.map((cupping) => (
                        <SelectItem
                          key={cupping.id}
                          value={cupping.id.toString()}
                        >
                          {cupping.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.cupping_id && (
                    <p className="text-destructive text-sm">
                      {errors.cupping_id}
                    </p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>{t("samples.new.fields.image")}</Label>
                  <div className="flex items-center gap-4">
                    {picturePreview ? (
                      <div className="relative">
                        <img
                          src={picturePreview}
                          alt="Preview"
                          className="h-24 w-24 rounded-lg border object-cover"
                        />
                        <button
                          type="button"
                          onClick={removePicture}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90 absolute -top-2 -right-2 rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="picture"
                        className="border-muted-foreground/25 hover:border-primary/50 flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors"
                      >
                        <Camera className="text-muted-foreground h-8 w-8" />
                        <span className="text-muted-foreground mt-1 text-xs">
                          {t("samples.new.fields.upload_photo")}
                        </span>
                      </label>
                    )}
                    <input
                      ref={fileInputRef}
                      id="picture"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePictureChange}
                      className="hidden"
                    />
                    {picturePreview && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={analyzeImage}
                        disabled={analyzingImage}
                        className="gap-2"
                      >
                        <Sparkles className="h-4 w-4" />
                        {analyzingImage
                          ? t("samples.new.ai_analyze") + "..."
                          : t("samples.new.ai_analyze")}
                      </Button>
                    )}
                  </div>
                  {(errors as any).picture && (
                    <p className="text-destructive text-sm">
                      {(errors as any).picture}
                    </p>
                  )}
                  {picturePreview && (
                    <p className="text-muted-foreground text-xs">
                      💡 {t("samples.new.ai_analyze_hint")}
                    </p>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">
                    {t("samples.new.fields.description")}
                  </Label>
                  <Input
                    id="description"
                    value={data.description}
                    onChange={(e) => setData("description", e.target.value)}
                    placeholder={t(
                      "samples.new.fields.description_placeholder",
                    )}
                  />
                  {errors.description && (
                    <p className="text-destructive text-sm">
                      {errors.description}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="origin">
                    {t("samples.new.fields.origin")}
                  </Label>
                  <Input
                    id="origin"
                    value={data.origin}
                    onChange={(e) => setData("origin", e.target.value)}
                    placeholder={t("samples.new.fields.origin_placeholder")}
                  />
                  {errors.origin && (
                    <p className="text-destructive text-sm">{errors.origin}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="land">{t("samples.new.fields.land")}</Label>
                  <Input
                    id="land"
                    value={data.land}
                    onChange={(e) => setData("land", e.target.value)}
                    placeholder={t("samples.new.fields.land_placeholder")}
                  />
                  {errors.land && (
                    <p className="text-destructive text-sm">{errors.land}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="producer">
                    {t("samples.new.fields.producer")}
                  </Label>
                  <Input
                    id="producer"
                    value={data.producer}
                    onChange={(e) => setData("producer", e.target.value)}
                    placeholder={t("samples.new.fields.producer_placeholder")}
                  />
                  {errors.producer && (
                    <p className="text-destructive text-sm">
                      {errors.producer}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="process">
                    {t("samples.new.fields.process")}
                  </Label>
                  <Select
                    value={data.process}
                    onValueChange={(value) => setData("process", value)}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "samples.new.fields.process_placeholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {processOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.process && (
                    <p className="text-destructive text-sm">{errors.process}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="variety">
                    {t("samples.new.fields.variety")}
                  </Label>
                  <Combobox
                    options={varietalOptions}
                    value={data.variety}
                    onChange={(value) => setData("variety", value)}
                    placeholder={t("samples.new.fields.variety_placeholder")}
                  />
                  {errors.variety && (
                    <p className="text-destructive text-sm">{errors.variety}</p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={processing}>
                  {t("samples.new.actions.create")}
                </Button>
                <Link href={samplesPath()}>
                  <Button type="button" variant="outline">
                    {t("samples.new.actions.cancel")}
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
